import { useEffect, useState } from 'react';
import MochiGrowthChart from '../components/mochi/MochiGrowthChart';
import MochiVaccineTimeline from '../components/mochi/MochiVaccineTimeline';
import MochiEnglishLearning from '../components/mochi/MochiEnglishLearning';
import { useT } from '../i18n';
import { api } from '../lib/api';
import type { MochiVaccinationsResponse, MochiWeightPoint } from '../types/mochi';

type LoadState = 'loading' | 'ready' | 'error';
type MochiTab = 'growth' | 'vaccines' | 'englishLearning';

export default function Mochi() {
  const t = useT();
  const [state, setState] = useState<LoadState>('loading');
  const [points, setPoints] = useState<MochiWeightPoint[]>([]);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState<MochiTab>('growth');
  const [vaccineState, setVaccineState] = useState<LoadState>('loading');
  const [vaccinePlan, setVaccinePlan] = useState<MochiVaccinationsResponse | null>(null);
  const [vaccineError, setVaccineError] = useState('');

  useEffect(() => {
    let cancelled = false;
    api.mochiGrowth()
      .then((response) => {
        if (cancelled) return;
        setPoints(response.points);
        setState('ready');
      })
      .catch((reason: unknown) => {
        if (cancelled) return;
        setError(reason instanceof Error ? reason.message : 'unknown error');
        setState('error');
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (activeTab !== 'vaccines' || vaccinePlan || vaccineState === 'error') return;
    let cancelled = false;
    api.mochiVaccinations()
      .then((response) => {
        if (cancelled) return;
        setVaccinePlan(response);
        setVaccineState('ready');
      })
      .catch((reason: unknown) => {
        if (cancelled) return;
        setVaccineError(reason instanceof Error ? reason.message : 'unknown error');
        setVaccineState('error');
      });
    return () => {
      cancelled = true;
    };
  }, [activeTab, vaccinePlan, vaccineState]);

  return (
    <section className="flex flex-col gap-8">
      <header className="max-w-4xl space-y-3">
        <p className="font-mono text-xs uppercase tracking-[0.24em] text-coffee">
          {t('mochi.kicker')}
        </p>
        <h1 className="text-4xl font-semibold leading-tight text-cocoa md:text-6xl">Mochi</h1>
        <p className="max-w-2xl leading-relaxed text-coffee">{t('mochi.subtitle')}</p>
      </header>

      <div className="relative flex min-w-0 flex-col gap-8">
        <div
          role="tablist"
          aria-label={t('mochi.tabs.label')}
          className="flex w-full flex-wrap rounded-xl border border-neutral-200 bg-white/65 p-1 shadow-soft md:w-fit 2xl:absolute 2xl:right-full 2xl:top-0 2xl:mr-10 2xl:w-48 2xl:flex-col 2xl:items-stretch 2xl:gap-1 2xl:p-2"
        >
          {(['growth', 'vaccines', 'englishLearning'] as const).map((tab) => (
            <button
              key={tab}
              type="button"
              role="tab"
              id={`mochi-tab-${tab}`}
              aria-controls={`mochi-panel-${tab}`}
              aria-selected={activeTab === tab}
              onClick={() => setActiveTab(tab)}
              className={`rounded-lg px-4 py-2.5 text-left text-sm font-medium transition 2xl:w-full 2xl:px-4 2xl:py-3 ${activeTab === tab ? 'bg-cocoa text-white shadow-sm' : 'text-coffee hover:bg-white hover:text-cocoa'}`}
            >
              {t(`mochi.tabs.${tab}`)}
            </button>
          ))}
        </div>

        <div id="mochi-panel-growth" role="tabpanel" aria-labelledby="mochi-tab-growth" hidden={activeTab !== 'growth'}>
          {state === 'loading' && <p className="font-mono text-sm text-coffee">{t('common.loading')}</p>}
          {state === 'error' && <p className="font-mono text-sm text-cocoa">{t('mochi.error', { error })}</p>}
          {state === 'ready' && points.length === 0 && <div className="border-y border-neutral-200 py-8 text-coffee">{t('mochi.empty')}</div>}
          {state === 'ready' && points.length > 0 && <MochiGrowthChart points={points} />}
        </div>

        <div id="mochi-panel-vaccines" role="tabpanel" aria-labelledby="mochi-tab-vaccines" hidden={activeTab !== 'vaccines'}>
          {vaccineState === 'loading' && <p className="font-mono text-sm text-coffee">{t('common.loading')}</p>}
          {vaccineState === 'error' && <p className="font-mono text-sm text-cocoa">{t('mochi.vaccine.error', { error: vaccineError })}</p>}
          {vaccineState === 'ready' && vaccinePlan && <MochiVaccineTimeline plan={vaccinePlan} />}
        </div>

        <div id="mochi-panel-englishLearning" role="tabpanel" aria-labelledby="mochi-tab-englishLearning" hidden={activeTab !== 'englishLearning'}>
          {activeTab === 'englishLearning' && <MochiEnglishLearning />}
        </div>
      </div>
    </section>
  );
}
